import type {
  ColumnConfigEntry,
  ColumnConfigSource,
  ExtensionColumnMetadata,
  ExtensionColumnSelection,
} from '@erp/shared-types';
export type ColumnFieldOption = Pick<
  ColumnConfigEntry,
  'apiField' | 'source'
> & {
  label: string;
  description?: string;
  format: NonNullable<ColumnConfigEntry['format']>;
};

/** Picker identity is separate from apiField, which is empty for extensions. */
export function columnSourceKey(column: {
  apiField: string;
  source?: ColumnConfigSource;
}) {
  const source = column.source;
  return source?.kind === 'extension'
    ? JSON.stringify([
        'extension',
        source.extensionId.toLowerCase(),
        source.field,
        source.relation ?? null,
      ])
    : column.apiField;
}

export function extensionColumnOptions(
  sources: ExtensionColumnMetadata[],
): ColumnFieldOption[] {
  return sources.flatMap((field) => {
    if (!field.fieldKey || !field.fieldType || field.issues.length) return [];
    const formats: Partial<
      Record<
        NonNullable<ExtensionColumnMetadata['fieldType']>,
        ColumnFieldOption['format']
      >
    > = {
      numeric: 'number',
      currency: 'currency',
      integer: 'integer',
      float: 'float',
      date: 'date',
      datetime: 'date',
    };
    return [
      {
        apiField: '',
        label: field.label ?? field.fieldId,
        description: field.extensionName ?? field.extensionId,
        format: formats[field.fieldType] ?? 'string',
        source: {
          kind: 'extension',
          extensionId: field.extensionId.toLowerCase(),
          field: field.fieldId,
          cell: 'default',
          relation: field.relation,
        },
      },
    ];
  });
}

export function configuredColumn(
  field: ColumnFieldOption,
  header: string,
): Omit<ColumnConfigEntry, 'id'> {
  const extension = field.source?.kind === 'extension';
  return {
    header: header.trim(),
    apiField: field.apiField,
    ...(field.source ? { source: field.source } : {}),
    ...(extension ? { sortable: false, filterable: false } : {}),
    alignRight: [
      'currency',
      'currency4',
      'number',
      'integer',
      'float',
    ].includes(field.format),
    format: field.format,
  };
}

/** Only visible extension columns request values; duplicate instances share a read. */
export function selectedExtensionColumns(
  columns: ColumnConfigEntry[],
): ExtensionColumnSelection[] {
  const selections = new Map<string, ExtensionColumnSelection>();
  for (const column of columns) {
    const source = column.source;
    if (column.hidden || source?.kind !== 'extension') continue;
    selections.set(columnSourceKey(column), {
      extensionId: source.extensionId.toLowerCase(),
      fieldId: source.field,
      ...(source.relation ? { relation: source.relation } : {}),
    });
  }
  return [...selections.values()];
}

/** Response metadata is keyed by source, so every column instance of a source shares it. */
export function extensionColumnMetadata(
  metadata: ExtensionColumnMetadata[] | undefined,
  source: ColumnConfigSource | undefined,
): ExtensionColumnMetadata | undefined {
  if (source?.kind !== 'extension') return undefined;
  return metadata?.find(
    (entry) =>
      entry.extensionId.toLowerCase() === source.extensionId.toLowerCase() &&
      entry.fieldId === source.field &&
      (entry.relation ?? null) === (source.relation ?? null),
  );
}
